import { Injectable, ViewContainerRef, ComponentRef } from '@angular/core';
import { MarkerDrawerComponent } from './marker-drawer/marker-drawer.component';
import * as L from 'leaflet';
import { GeoManagerCaptions, MarkerData, MarkerGroup, ProcessedMarkerGroup } from './geo-manager.interfaces';

@Injectable()
export class GeoManagerService {
  
  /**
   * Generates a deterministic hex color based on an index
   * Uses HSL color space to ensure good color distribution
   * Supports up to 200+ distinct colors
   */
  private generateDeterministicColor(index: number): string {
    // Golden ratio conjugate for even distribution
    const goldenRatio = 0.618033988749;
    
    // Use different strategies for better color distribution
    let hue: number;
    let saturation: number;
    let lightness: number;
    
    if (index < 12) {
      // First 12 colors - use the classic web-safe colors for consistency
      const baseColors = [
        '#ef4444', // red
        '#3b82f6', // blue  
        '#22c55e', // green
        '#a855f7', // purple
        '#f97316', // orange
        '#ec4899', // pink
        '#6366f1', // indigo
        '#eab308', // yellow
        '#14b8a6', // teal
        '#06b6d4', // cyan
        '#10b981', // emerald
        '#84cc16'  // lime
      ];
      return baseColors[index];
    }
    
    // For colors beyond the first 12, generate using HSL
    const adjustedIndex = index - 12;
    
    // Create multiple rings of hue with different saturations and lightness
    const ring = Math.floor(adjustedIndex / 24); // 24 colors per ring
    const ringPosition = adjustedIndex % 24;
    
    // Distribute hue evenly around the color wheel
    hue = (ringPosition * goldenRatio) % 1.0;
    
    // Vary saturation and lightness by ring to create distinct colors
    switch (ring % 4) {
      case 0: // Vibrant colors
        saturation = 0.7 + (ringPosition % 3) * 0.1; // 0.7-0.9
        lightness = 0.5 + (ringPosition % 2) * 0.1;  // 0.5-0.6
        break;
      case 1: // Darker colors
        saturation = 0.6 + (ringPosition % 3) * 0.1; // 0.6-0.8
        lightness = 0.3 + (ringPosition % 3) * 0.1;  // 0.3-0.5
        break;
      case 2: // Lighter colors
        saturation = 0.5 + (ringPosition % 3) * 0.1; // 0.5-0.7
        lightness = 0.6 + (ringPosition % 3) * 0.1;  // 0.6-0.8
        break;
      case 3: // Muted colors
        saturation = 0.4 + (ringPosition % 3) * 0.1; // 0.4-0.6
        lightness = 0.4 + (ringPosition % 3) * 0.1;  // 0.4-0.6
        break;
      default:
        saturation = 0.7;
        lightness = 0.5;
    }
    
    return this.hslToHex(hue * 360, saturation * 100, lightness * 100);
  }
  
  /**
   * Converts HSL to hex color
   */
  private hslToHex(h: number, s: number, l: number): string {
    h = h / 360;
    s = s / 100;
    l = l / 100;

    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };

    let r, g, b;

    if (s === 0) {
      r = g = b = l; // achromatic
    } else {
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;
      r = hue2rgb(p, q, h + 1/3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1/3);
    }

    const toHex = (c: number) => {
      const hex = Math.round(c * 255).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  // Default captions
  private defaultCaptions: GeoManagerCaptions = {
    title: 'GeoManager',
    selectionModeLabel: 'Modo Selección',
    selectedMarkersTitle: 'Marcadores Seleccionados',
    clearAllButton: 'Limpiar Todo',
    noMarkersSelected: 'Ningún marcador seleccionado',
    markerCoordinatesLabel: 'Coordenadas del Marcador',
    removeFromSelectionButton: 'Eliminar',
    markerDetailsTitle: 'Detalles del Marcador',
    locationLabel: 'Ubicación',
    latitudeLabel: 'Latitud',
    longitudeLabel: 'Longitud',
    additionalInfoLabel: 'Información Adicional',
    // Geofence captions
    geofenceModeLabel: 'Mostrar Geocercas',
    geofencesTitle: 'Gestión Geocercas',
    noGeofencesCreated: 'No hay geocercas creadas',
    noGeofencesMessage: 'No hay geocercas creadas. Use las herramientas de dibujo para crear nuevas geocercas.',
    createGeofenceButton: 'Crear Geocerca',
    editGeofenceButton: 'Editar',
    deleteGeofenceButton: 'Eliminar',
    removeGeofenceButton: 'Eliminar',
    saveGeofenceButton: 'Guardar',
    cancelGeofenceButton: 'Cancelar',
    cancelDrawingButton: 'Cancelar Dibujo',
    geofenceNameLabel: 'Nombre de la Geocerca',
    geofenceDescriptionLabel: 'Descripción',
    drawPolygonButton: 'Dibujar Polígono',
    drawRectangleButton: 'Dibujar Rectángulo',
    drawCircleButton: 'Dibujar Círculo',
    // Additional geofence captions for UI elements
    addGeofenceLabel: 'Agregar',
    createPolygonTooltip: 'Crear polígono',
    createRectangleTooltip: 'Crear rectángulo',
    createCircleTooltip: 'Crear círculo',
    drawOnMapLabel: 'Dibujar en el Mapa',
    drawOnMapInstruction: 'Haz clic en el mapa para comenzar a dibujar tu geocerca',
    cancelDrawingInstruction: 'Presiona ESC o el botón X para cancelar',
    createGeofenceFormTitle: 'Crear Geocerca',
    editGeofenceFormTitle: 'Editar Geocerca',
    geofenceNamePlaceholder: 'Nombre de la geocerca',
    geofenceDescriptionPlaceholder: 'Descripción opcional',
    geofenceColorLabel: 'Color',
    drawOnMapButton: 'Dibujar en Mapa',
    markersCountLabel: 'markers',
    noGeofencesEmptyMessage: 'No hay geocercas creadas. Use las herramientas de dibujo para crear nuevas geocercas.',
    selectionInstruction: 'Haz clic en los marcadores del mapa para seleccionarlos',
    // Empty state captions
    noGeofencesTitle: 'No hay geocercas',
    noGeofencesSubtitle: 'Crea tu primera geocerca para comenzar'
  };

  /**
   * Process marker groups with deterministic color assignment
   * Supports up to 200+ distinct colors
   */
  processMarkerGroups(markerGroups: MarkerGroup[]): ProcessedMarkerGroup[] {
    return markerGroups.map((group, index) => {
      let fillColor: string;
      let groupColor: string;
      
      if (group.groupColor) {
        // If groupColor is already a hex color, use it directly
        if (group.groupColor.startsWith('#')) {
          fillColor = group.groupColor;
          groupColor = group.groupColor;
        } else {
          // Legacy named colors - map to index for backward compatibility
          const namedColorMap: { [key: string]: number } = {
            'red': 0, 'blue': 1, 'green': 2, 'purple': 3,
            'orange': 4, 'pink': 5, 'indigo': 6, 'yellow': 7,
            'teal': 8, 'cyan': 9, 'emerald': 10, 'lime': 11
          };
          const colorIndex = namedColorMap[group.groupColor] ?? index;
          fillColor = this.generateDeterministicColor(colorIndex);
          groupColor = fillColor;
        }
      } else {
        // Auto-assign colors using deterministic generation
        fillColor = this.generateDeterministicColor(index);
        groupColor = fillColor;
      }

      return {
        ...group,
        groupColor: groupColor,
        bgColor: fillColor, // No more Tailwind classes - use hex directly
        fillColor: fillColor
      };
    });
  }

  /**
   * Merge user captions with defaults
   */
  mergeCaptions(userCaptions: GeoManagerCaptions = {}): GeoManagerCaptions {
    return { ...this.defaultCaptions, ...userCaptions };
  }

  /**
   * Create Leaflet map instance
   */
  createMap(elementId: string, center: [number, number], zoom: number): L.Map {
    const map = L.map(elementId).setView(center, zoom);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors'
    }).addTo(map);
    
    // Force map to resize after a short delay to ensure container is properly sized
    setTimeout(() => {
      map.invalidateSize();
    }, 100);

    return map;
  }

  /**
   * Create Leaflet map instance with editable capabilities
   */
  createEditableMap(elementId: string, center: [number, number], zoom: number): L.Map {
    const map = L.map(elementId, {
      editable: true  // Enable Leaflet.Editable
    }).setView(center, zoom);
    
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors'
    }).addTo(map);
    
    // Force map to resize after a short delay to ensure container is properly sized
    setTimeout(() => {
      map.invalidateSize();
    }, 100);

    return map;
  }

  /**
   * Fit map to show all markers
   */
  fitMapToMarkers(map: L.Map, positions: [number, number][]): void {
    if (positions.length === 0) return;
    
    if (positions.length === 1) {
      // Single marker - center on it with default zoom
      map.setView(positions[0], 13);
      return;
    }

    // Multiple markers - calculate bounds and fit
    const group = new L.FeatureGroup();
    positions.forEach(position => {
      L.marker(position).addTo(group);
    });
    
    // Add some padding to the bounds (10% of the view)
    const bounds = group.getBounds();
    map.fitBounds(bounds, { 
      padding: [20, 20],
      maxZoom: 15 // Prevent zooming in too much
    });
    
  }

  /**
   * Create marker element HTML from template
   */
  createMarkerElement(markerTemplate: HTMLElement, group: ProcessedMarkerGroup, marker?: MarkerData): string {
    const markerElement = markerTemplate.cloneNode(true) as HTMLElement;
    
    // Update marker colors
    const markerBody = markerElement.querySelector('path') as SVGPathElement;
    const markerDot = markerElement.querySelector('circle[r="3"]') as SVGCircleElement;
    const innerCircle = markerElement.querySelector('circle[r="6"]') as SVGCircleElement;
    
    if (markerBody) markerBody.setAttribute('fill', group.fillColor);
    
    // Only render the circles if there's no order number
    if (marker?.order === undefined) {
      if (markerDot) markerDot.setAttribute('fill', group.fillColor);
    } else {
      // Remove both circles completely when there's an order number
      if (markerDot) markerDot.remove();
      if (innerCircle) innerCircle.remove();
    }

    // Add order number if present
    if (marker?.order !== undefined) {
      // Create text element for the order number
      const svg = markerElement.querySelector('svg');
      if (svg) {
        const textElement = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        textElement.setAttribute('x', '16');
        textElement.setAttribute('y', '17'); // Perfect position as tested by user
        textElement.setAttribute('text-anchor', 'middle');
        textElement.setAttribute('dominant-baseline', 'middle');
        textElement.setAttribute('fill', 'white');
        textElement.setAttribute('font-weight', 'bold');
        textElement.setAttribute('font-family', 'system-ui, -apple-system, sans-serif');
        
        // Determine font size based on number of digits
        const orderStr = marker.order.toString();
        let fontSize = '10';
        if (orderStr.length === 1) {
          fontSize = '12';
        } else if (orderStr.length === 2) {
          fontSize = '10';
        } else if (orderStr.length === 3) {
          fontSize = '8';
        }
        
        textElement.setAttribute('font-size', fontSize);
        textElement.textContent = orderStr;
        
        // Add a subtle stroke for better readability
        textElement.setAttribute('stroke', 'rgba(0,0,0,0.3)');
        textElement.setAttribute('stroke-width', '0.5');
        
        svg.appendChild(textElement);
      }
    }

    return markerElement.outerHTML;
  }

  /**
   * Create popup element HTML from template
   */
  createPopupElement(
    popupTemplate: HTMLElement, 
    group: ProcessedMarkerGroup, 
    marker: MarkerData, 
    position: [number, number]
  ): string {
    const popupElement = popupTemplate.cloneNode(true) as HTMLElement;
    
    // Update popup content
    const titleElement = popupElement.querySelector('h3') as HTMLElement;
    const subtitleElement = popupElement.querySelector('.popup-subtitle') as HTMLElement;
    const descriptionElement = popupElement.querySelector('.popup-description') as HTMLElement;
    const colorElement = popupElement.querySelector('.rounded-full') as HTMLElement;
    const groupNameElements = popupElement.querySelectorAll('.text-xs');
    const groupNameElement = groupNameElements[0] as HTMLElement;
    const coordsElement = groupNameElements[1] as HTMLElement;
    const customPropsElement = popupElement.querySelector('.popup-custom-props') as HTMLElement;
    
    if (titleElement) titleElement.textContent = marker.title;
    if (subtitleElement) subtitleElement.textContent = marker.subtitle;
    if (descriptionElement) descriptionElement.textContent = marker.description;
    if (colorElement) {
      colorElement.className = 'w-3 h-3 rounded-full mr-2 border border-gray-300';
      colorElement.style.backgroundColor = group.bgColor;
    }
    if (groupNameElement) groupNameElement.textContent = group.groupName;
    if (coordsElement) coordsElement.textContent = `Lat: ${position[0].toFixed(4)}, Lng: ${position[1].toFixed(4)}`;
    
    // Handle custom properties section
    if (customPropsElement) {
      if (marker.customProperties && Object.keys(marker.customProperties).length > 0) {
        // Show the section and populate with custom properties
        customPropsElement.style.display = 'block';
        const propsHtml = Object.entries(marker.customProperties)
          .map(([key, value]) => `<div class="flex justify-between text-xs"><span class="font-medium">${key}:</span><span>${Array.isArray(value) ? value.join(', ') : value}</span></div>`)
          .join('');
        customPropsElement.innerHTML = propsHtml;
      } else {
        // Hide the section when no custom properties
        customPropsElement.style.display = 'none';
      }
    }

    return popupElement.outerHTML;
  }

  /**
   * Create selected marker element for selection drawer
   */
  createSelectedMarkerElement(
    selectionMarkerTemplate: HTMLElement,
    marker: MarkerData, 
    group: ProcessedMarkerGroup,
    captions: GeoManagerCaptions,
    onRemove: (marker: MarkerData, group: ProcessedMarkerGroup) => void,
    onCenterMap: (marker: MarkerData) => void
  ): HTMLElement {
    const markerElement = selectionMarkerTemplate.cloneNode(true) as HTMLElement;
    
    // Populate marker data
    const nameElement = markerElement.querySelector('.marker-name');
    if (nameElement) {
      nameElement.textContent = marker.title || 'Marcador sin nombre';
    }
    
    const locationElement = markerElement.querySelector('.marker-location');
    if (locationElement) {
      locationElement.textContent = `${marker.latitude?.toFixed(6)}, ${marker.longitude?.toFixed(6)}`;
    }
    
    // Populate group information
    const groupElement = markerElement.querySelector('.marker-group');
    if (groupElement) {
      groupElement.textContent = group.groupName;
    }
    
    // Set the marker color
    const colorElement = markerElement.querySelector('.marker-color');
    if (colorElement) {
      colorElement.className = 'w-4 h-4 rounded-full mr-3 border border-gray-300';
      (colorElement as HTMLElement).style.backgroundColor = group.bgColor;
    }
    
    // Set up remove button
    const removeButton = markerElement.querySelector('.remove-marker-btn');
    if (removeButton) {
      (removeButton as HTMLElement).title = captions.removeFromSelectionButton || 'Quitar de la selección';
      removeButton.addEventListener('click', (e) => {
        e.stopPropagation();
        onRemove(marker, group);
      });
    }
    
    // Add click handler to center on marker (only on the main area, not the button)
    const clickableArea = markerElement.querySelector('.flex-1');
    if (clickableArea) {
      clickableArea.addEventListener('click', () => {
        onCenterMap(marker);
      });
    }
    
    return markerElement;
  }

  /**
   * Clean up a ViewContainerRef and force DOM cleanup
   */
  cleanupContainer(container: ViewContainerRef): void {
    if (!container) return;

    // Clear the container
    container.clear();
    // Force remove any lingering DOM elements
    const containerElement = container.element.nativeElement;
    if (containerElement) {
      while (containerElement.firstChild) {
        console.log('Removing lingering child element:', containerElement.firstChild);
        containerElement.removeChild(containerElement.firstChild);
      }
      
      // Look for orphaned elements in parent
      const parentElement = containerElement.parentElement;
      if (parentElement) {
        const orphanedElements = parentElement.querySelectorAll('.drawer-panel, app-marker-drawer, .selection-drawer-panel');
        orphanedElements.forEach((element, index) => {
          console.log(`Removing orphaned element ${index + 1}:`, element);
          element.remove();
        });
      }
    }
  }

  /**
   * Toggle visibility of a any element
   */
  toggleElementVisibility(element: HTMLElement, isVisible: boolean): void {
    element && element.classList.toggle('hidden', !isVisible);
  }

  /**
   * Create and configure a drawer component
   */
  createDrawerComponent(
    container: ViewContainerRef,
    marker: MarkerData,
    group: ProcessedMarkerGroup,
    captions: GeoManagerCaptions,
    preventDismiss: boolean,
    onClose: () => void
  ): ComponentRef<MarkerDrawerComponent> {
    // Ensure container is clean
    this.cleanupContainer(container);
    
    // Create the drawer component
    const componentRef = container.createComponent(MarkerDrawerComponent);
    
    // Set the component inputs
    componentRef.instance.marker = marker;
    componentRef.instance.group = group;
    componentRef.instance.captions = captions;
    componentRef.instance.preventDismiss = preventDismiss;
    componentRef.instance.isOpen = false; // Start closed for animation
    
    // Subscribe to close event
    componentRef.instance.close.subscribe(onClose);
    
    return componentRef;
  }

  /**
   * Animate drawer open
   */
  animateDrawerOpen(componentRef: ComponentRef<MarkerDrawerComponent>): Promise<void> {
    return new Promise((resolve) => {
      // Trigger change detection to ensure component is rendered
      componentRef.changeDetectorRef.detectChanges();
      
      // Small delay to ensure DOM is ready, then animate open
      requestAnimationFrame(() => {
        componentRef.instance.isOpen = true;
        componentRef.changeDetectorRef.detectChanges();
        console.log('Drawer animated to open state');
        
        // Mark animation as complete after transition
        setTimeout(() => {
          resolve();
        }, 350);
      });
    });
  }

  /**
   * Animate drawer close and destroy
   */
  animateDrawerClose(componentRef: ComponentRef<MarkerDrawerComponent>): Promise<void> {
    return new Promise((resolve) => {
      // Animate drawer closed
      componentRef.instance.isOpen = false;
      componentRef.changeDetectorRef.detectChanges();
      console.log('Drawer animated to closed state');
      
      // Wait for animation to complete, then destroy component
      setTimeout(() => {
        this.destroyDrawerComponent(componentRef);
        resolve();
      }, 350); // Match CSS transition duration
    });
  }

  /**
   * Destroy drawer component and clean up DOM
   */
  destroyDrawerComponent(componentRef: ComponentRef<MarkerDrawerComponent>): void {
    if (!componentRef) return;

    console.log('Destroying drawer component');
    
    // Get the actual DOM element before destroying
    const hostElement = componentRef.location.nativeElement;
    console.log('Host element found:', hostElement);
    
    // Destroy the component reference
    componentRef.destroy();
    
    // Force remove the DOM element if it still exists
    if (hostElement && hostElement.parentNode) {
      console.log('Manually removing host element from DOM');
      hostElement.parentNode.removeChild(hostElement);
    }
    
    console.log('Drawer component destroyed');
  }

  /**
   * Log marker click event for debugging
   */
  logMarkerClick(marker: MarkerData, group: ProcessedMarkerGroup): void {
    console.log('🖱️ Marker clicked (details disabled):', {
      marker: marker.title,
      group: group.groupName,
      coordinates: [marker.latitude, marker.longitude],
      fullMarkerData: marker,
      fullGroupData: group
    });
  }

  /**
   * Log component initialization
   */
  logInitialization(config: {
    displayMode: string;
    enableSelectionMode: boolean;
    disableMarkerDetails: boolean;
    autoSelectFirstMarker: boolean;
    preventDismissDetails: boolean;
    markerGroupsCount: number;
  }): void {
    console.log('🗺️ GeoManager initialized with settings:', config);
  }

  /**
   * Calculate number of markers inside a geofence
   */
  calculateMarkersInGeofence(geofence: any, allMarkers: { latitude: number; longitude: number }[]): number {
    let count = 0;

    for (const marker of allMarkers) {
      if (this.isMarkerInsideGeofence(marker, geofence)) {
        count++;
      }
    }

    return count;
  }

  /**
   * Get all markers inside a geofence
   */
  getMarkersInGeofence(geofence: any, allMarkers: { latitude: number; longitude: number }[]): { latitude: number; longitude: number }[] {
    const markersInside: { latitude: number; longitude: number }[] = [];

    for (const marker of allMarkers) {
      if (this.isMarkerInsideGeofence(marker, geofence)) {
        markersInside.push(marker);
      }
    }

    return markersInside;
  }

  /**
   * Check if a marker is inside a geofence
   */
  private isMarkerInsideGeofence(marker: { latitude: number; longitude: number }, geofence: any): boolean {
    const point = L.latLng(marker.latitude, marker.longitude);

    switch (geofence.type) {
      case 'circle':
        const center = geofence.center || [0, 0];
        const centerLatLng = L.latLng(center[0], center[1]);
        const distance = point.distanceTo(centerLatLng);
        return distance <= (geofence.radius || 0);

      case 'polygon':
      case 'rectangle':
        if (!geofence.coordinates || geofence.coordinates.length === 0) return false;
        return this.isPointInPolygon(point, geofence.coordinates);

      default:
        return false;
    }
  }

  /**
   * Check if a point is inside a polygon using ray casting algorithm
   */
  private isPointInPolygon(point: L.LatLng, coordinates: [number, number][]): boolean {
    const x = point.lat;
    const y = point.lng;
    let inside = false;

    for (let i = 0, j = coordinates.length - 1; i < coordinates.length; j = i++) {
      const xi = coordinates[i][0];
      const yi = coordinates[i][1];
      const xj = coordinates[j][0];
      const yj = coordinates[j][1];

      if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
        inside = !inside;
      }
    }

    return inside;
  }
}
